import { action, observable, reaction, makeObservable } from 'mobx';
import { BaseListViewStore } from 'core/stores';
import LookupStore from './LookupStore';
import { lookupRouteName, sourceOfChange } from 'common/constants';
import { translate, QueryUtility, makePlural, isSuperAdminOrganization } from 'core/utils';
import { localStorageHandler } from 'core/services';
import { orderBy } from '@progress/kendo-data-query';
import _ from 'lodash';
import { LookupEdit } from '../pages';
import { BaasicDropdownViewStore } from 'common/stores';
import { CarrierServiceStore, CarrierStore } from 'modules/carrier/stores';
import { WarehouseOrganizationInfoStore } from 'modules/warehouse/stores';
import { ApplicationStore } from 'platform/application/stores';


class LookupViewStore extends BaseListViewStore {
	@observable selectedLookup = null;
	allList = {
		tableData: {
			columns: [
				{
					key: 'value',
					title: 'SELECTLOOKUP',
					resizable: false,
					sortable: true,
					selected: true,
				},
			],
			actions: {},
			sortObject: [
				{
					field: 'value',
					dir: 'asc',
				},
			],
		},
		queryUtility: new QueryUtility(this.rootStore),
	};

	defaultActions = {
		onEdit: async item => this.routes.edit(item.id),
		onDelete: async item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
	};

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteLookup(this.rootStore.viewStore.selectedConfirmModal.data),
			message: [
				'LOOKUPDELETECONFIRM.LOOKUP',
				{ name: item.name ? item.name : item[Object.keys(item)[1]], lookup: this.selectedLookup.parameters.itemName },
			],
			data: item,
		};
	};

	constructor(rootStore) {
		super(rootStore, {
			listStore: new LookupStore(rootStore),
			routes: {
				detail: id => this.rootStore.routerStore.goTo('master.main.lookup.preview', { id }),
				edit: id => this.rootStore.routerStore.setQueryParams({ editId: id }),
				create: () => this.rootStore.routerStore.setQueryParams({ create: null }),
				import: () => this.rootStore.routerStore.setQueryParams({ importData: true }),
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission('fulfillmentLookupSection.update'),
				onDelete: () => this.authStore.hasPermission('fulfillmentLookupSection.delete'),
				create: () => this.authStore.hasPermission('fulfillmentLookupSection.create'),
			},
			tableData: {
				columns: [
					{
						key: '',
						title: '',
						sortable: false,
						onClick: item => this.routes.edit(item.id),
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onDelete: async item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
				},
				sortObject: [],
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound async initialize() {
		this.suspend();
		this.isSuperAdminOrganization = isSuperAdminOrganization(this.rootStore);
		if (this.isSuperAdminOrganization) {
			await this.rootStore.viewStore.handleSellerForChildViewStore(this, false);
		}
		this.queryUtility.filter.orderBy = this.allList.tableData.sortObject[0].field;
		this.queryUtility.filter.orderDirection = this.allList.tableData.sortObject[0].dir;
		await this.queryUtility.initialize(this.findLookup);

		this.initializeLookups();

		// Do early translate for itemName
		_.each(this.lookups, (val, key, col) => {
			if (!_.has(col[key], 'itemName')) {
				col[key].itemName = translate(val.columnName).toLowerCase();
			}
			if (!_.has(col[key], 'columnKey')) {
				col[key].columnKey = 'name';
			}
		});
		this.lookupDropdownStore = new BaasicDropdownViewStore();
		await this.lookupDropdownStore.initialize({
			staticOptions: _.map(this.lookups, item => {
				if (item) {
					return { value: translate(item.columnName), label: translate(item.columnName), parameters: item };
				}
			}),
			onChangeCallback: value => {
				this.onLookupSelect(value);
			},
			initCallback: this.onLookupDropdownStoreInit,
		});

		this.setHeaderModes();

		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.lookup.list') {
					return;
				}
				const { editId, create } = state.queryParams;

				if (create !== undefined) {
					state.queryParams.page = undefined;
					state.queryParams.rpp = undefined;
				}

				if (editId !== undefined || create !== undefined) {
					this.openModal({
						onClose: async () => this.closeEditModal(),
						data: this,
						component: LookupEdit,
					});
				}
			}
		);
		this.resume();
	}

	@action.bound
	initializeLookups = () => {
		this.lookups = [
			{
				lookup: lookupRouteName.convertService,
				columnName: 'LOOKUP.CONVERTSERVICE',
				fields: [
					{
						key: 'name',
						rules: 'required|string',
					},
					{
						key: 'carrierServiceId',
						path: 'carrierService.name',
						label: 'LOOKUP.CARRIERSERVICE',
						isDropdown: true,
						store: new CarrierServiceStore(this.rootStore),
						rules: 'required',
					},
					{
						key: 'channelTypeId',
						path: 'channelType.name',
						label: 'LOOKUP.CHANNELTYPE',
						rules: 'required',
						isDropdown: true,
						store: new LookupStore(this.rootStore),
						lookupRoute: lookupRouteName.channelType,
					},
				],
			},
			{
				lookup: lookupRouteName.convertCarrier,
				columnName: 'LOOKUP.CONVERTCARRIER',
				mainField: 'channelCarrierCode',
				fields: [
					{ 
						key: 'sellerId',
						path: 'seller.applicationTitle',
						label: 'LOOKUP.SELLER', 
						rules: 'required',
						isDropdown: true,
						store: new ApplicationStore(this.rootStore),
						itemTemplate: {
							value: 'id',
							label: 'applicationTitle',
						},
						keyToValidate: 'carrierId',
						filter: {
							sellerOnly: true,
						}
					},
					{
						key: 'channelTypeId',
						path: 'channelType.name',
						label: 'LOOKUP.CHANNELTYPE',
						rules: 'required',
						isDropdown: true,
						store: new LookupStore(this.rootStore),
						lookupRoute: lookupRouteName.channelType,
					},
					{
						key: 'name',
						rules: 'required|string',
					},
					{
						key: 'carrierId',
						path: 'carrier.name',
						label: 'LOOKUP.CARRIER',
						isDropdown: true,
						store: new CarrierStore(this.rootStore),
						rules: 'required',
					},
				],
			},
			{
				lookup: lookupRouteName.cancelReason,
				columnName: 'LOOKUP.CANCELREASON',
			},
			{
				lookup: lookupRouteName.confirmationType,
				columnName: 'LOOKUP.CONFIRMATIONTYPE',
			},
			{
				lookup: lookupRouteName.carrierType,
				columnName: 'LOOKUP.CARRIERTYPE',
			},
			{
				lookup: lookupRouteName.closeOutType,
				columnName: 'LOOKUP.CLOSEOUTTYPE',
			},
			{
				lookup: lookupRouteName.currency,
				columnName: 'LOOKUP.CURRENCY',
				fields: [
					{
						key: 'currency',
						rules: 'required|string',
					},
					{
						key: 'code',
						label: 'LOOKUP.CODE',
						rules: 'required|string',
					},
					{
						key: 'description',
						label: 'LOOKUP.DESCRIPTION',
						rules: 'required|string',
					},
				],
			},
			{
				lookup: lookupRouteName.warehouseActivityType,
				columnName: 'LOOKUP.WAREHOUSEACTIVITYTYPE',
			},
			{
				lookup: lookupRouteName.receivingStatus,
				columnName: 'LOOKUP.RECEIVINGSTATUS',
			},
			{
				lookup: lookupRouteName.receivingType,
				columnName: 'LOOKUP.RECEIVINGTYPE',
			},
			{
				lookup: lookupRouteName.channelType,
				columnName: 'LOOKUP.CHANNELTYPE',
				isEditDisabled: true,
			},
			{
				lookup: lookupRouteName.dimensionUnit,
				columnName: 'LOOKUP.DIMENSIONUNIT',
			},
			{
				lookup: lookupRouteName.orderStatus,
				columnName: 'LOOKUP.ORDERSTATUS',
			},
			{
				lookup: lookupRouteName.tagType,
				columnName: 'LOOKUP.TAGTYPE',
			},
			{
				lookup: lookupRouteName.onHoldReason,
				columnName: 'LOOKUP.ONHOLDREASON',
			},
			{
				lookup: lookupRouteName.returnReason,
				columnName: 'LOOKUP.RETURNREASON',
			},
			{
				lookup: lookupRouteName.replacementReason,
				columnName: 'LOOKUP.REPLACEMENTREASON',
			},
			{
				lookup: lookupRouteName.productCategory,
				columnName: 'LOOKUP.PRODUCTCATEGORY',
			},
			{
				lookup: lookupRouteName.orderType,
				columnName: 'LOOKUP.ORDERTYPE',
			},
			{
				lookup: lookupRouteName.condition,
				columnName: 'LOOKUP.CONDITION',
			},
			{
				lookup: lookupRouteName.orderLineStatus,
				columnName: 'LOOKUP.ORDERLINESTATUS',
			},
			{
				lookup: lookupRouteName.productStatus,
				columnName: 'LOOKUP.PRODUCTSTATUS',
			},
			{
				lookup: lookupRouteName.tag,
				columnName: 'LOOKUP.TAG',
				fields: [
					{
						key: 'name',
						rules: 'required|string',
					},
					{
						key: 'tagTypeId',
						path: 'tagType.name',
						label: 'LOOKUP.TAGTYPE',
						isDropdown: true,
						store: new LookupStore(this.rootStore),
						lookupRoute: lookupRouteName.tagType,
						rules: 'required',
					},
				],
			},
			{
				lookup: lookupRouteName.timezone,
				columnName: 'LOOKUP.TIMEZONE',
			},
			{
				lookup: lookupRouteName.weightUnit,
				columnName: 'LOOKUP.WEIGHTUNIT',
			},
			{
				lookup: lookupRouteName.warehouseStatus,
				columnName: 'LOOKUP.WAREHOUSESTATUS',
			},
			{
				lookup: lookupRouteName.box,
				columnName: 'LOOKUP.BOXLOOKUP',
				fields: [
					{
						key: 'name',
						label: 'LOOKUP.BOX.NAME',
						rules: 'required|string',
					},
					{
						key: 'length',
						label: 'LOOKUP.BOX.LENGTH',
						rules: 'required|numeric|numericMinValue|invalidCharacter',
					},
					{
						key: 'width',
						label: 'LOOKUP.BOX.WIDTH',
						rules: 'required|numeric|numericMinValue|invalidCharacter',
					},
					{
						key: 'height',
						label: 'LOOKUP.BOX.HEIGHT',
						rules: 'required|numeric|numericMinValue|invalidCharacter',
					},
				],
			},
			{
				lookup: lookupRouteName.carrierServiceType,
				columnName: 'LOOKUP.CARRIERSERVICETYPE',
			},
			{
				lookup: lookupRouteName.imageUploadSuccessStatus,
				columnName: 'LOOKUP.IMAGEUPLOADSUCCESSSTATUS',
			},
			{
				lookup: lookupRouteName.convertWmsCarrier,
				columnName: 'LOOKUP.CONVERTWMSCARRIER',
				fields: [
					{
						key: 'sellerId',
						path: 'seller.applicationTitle',
						label: 'LOOKUP.SELLER', 
						rules: 'required',
						isDropdown: true,
						store: new ApplicationStore(this.rootStore),
						itemTemplate: {
							value: 'id',
							label: 'applicationTitle',
						},
						keyToValidate: 'carrierId',
						filter: {
							sellerOnly: true,
						}
					},
					{
						key: 'warehouseId',
						path: 'warehouse.applicationTitle',
						label: 'LOOKUP.WAREHOUSE',
						rules: 'required',
						isDropdown: true,
						store: new ApplicationStore(this.rootStore),
						itemTemplate: {
							value: 'id',
							label: 'applicationTitle',
						},
						filter: {
							warehouseOnly: true,
						}
					},
					{
						key: 'carrierId',
						path: 'carrier.name',
						label: 'LOOKUP.CARRIER', 
						rules: 'required',
						isDropdown: true,
						store: new CarrierStore(this.rootStore),
					},
					{
						key: 'name',
						rules: 'required|string',
					},
				],
			},
		];
		if (this.isSuperAdminOrganization) {
			this.lookups = _.concat(this.lookups, [
				{
					lookup: lookupRouteName.carrierDeliveryStatus,
					columnName: 'LOOKUP.CARRIERDELIVERYSTATUS',
					fields: [
						{
							key: 'name',
							rules: 'required|string',
						},
						{
							key: 'carrierTypeId',
							path: 'carrierType.name',
							label: 'LOOKUP.CARRIERTYPE',
							isDropdown: true,
							store: new LookupStore(this.rootStore),
							lookupRoute: lookupRouteName.carrierType,
							rules: 'required',
						},
						{
							key: 'shipmentInfoDeliveryStatusId',
							path: 'shipmentInfoDeliveryStatus.name',
							label: 'LOOKUP.SHIPMENTINFODELIVERYSTATUS',
							isDropdown: true,
							store: new LookupStore(this.rootStore),
							lookupRoute: lookupRouteName.shipmentInfoDeliveryStatus,
							rules: 'required',
						},
					],
				},
				{
					lookup: lookupRouteName.shipmentInfoDeliveryStatus,
					columnName: 'LOOKUP.SHIPMENTINFODELIVERYSTATUS',
				},
			]);
		}
	};

	onLookupDropdownStoreInit = async () => {
		let matchingLookup = this.lookups.find(l => l.lookup === localStorageHandler.get('lookup-filter-key'));
		if (matchingLookup) {
			await this.onLookupSelect({
				value: translate(matchingLookup.columnName),
				label: translate(matchingLookup.columnName),
				parameters: matchingLookup,
			});
		} else {
			this.allList.queryUtility.initialize(() => {
				this.listStore.response.item = orderBy(
					_.map(this.lookups, item => {
						return {
							value: translate(item.columnName),
							parameters: item,
							label: translate(item.columnName),
						};
					}),
					this.allList.tableData.sortObject
				);
			}, false);
		}
	};

	@action.bound
	onAllLookupSort = () => {
		if (this.allList.tableData.sortObject[0].dir === 'asc') {
			this.allList.tableData.sortObject[0].dir = 'desc';
		} else {
			this.allList.tableData.sortObject[0].dir = 'asc';
		}
		this.queryUtility.filter.orderBy = this.allList.tableData.sortObject[0].field;
		this.queryUtility.filter.orderDirection = this.allList.tableData.sortObject[0].dir;
		this.listStore.response.item = orderBy(
			_.map(this.lookups, item => {
				return {
					value: translate(item.columnName),
					parameters: item,
					label: translate(item.columnName),
				};
			}),
			this.allList.tableData.sortObject
		);
	};

	@action.bound async onManagerSync(evt) {
		evt.preventDefault(true);
		this.suspend();
		try {
			await this.listStore.createLookup({}, lookupRouteName.managerSync, sourceOfChange.createLookup);
		} catch ({ statusCode, data }) {
			if (data && data.message) {
				this.rootStore.notificationStore.error(data.message);
			} else {
				this.rootStore.notificationStore.error([
					'LOOKUPSYNCERROR',
					{ name: this.selectedLookup.parameters.itemName, code: statusCode },
				]);
			}
		}
		this.resume();
		await this.queryUtility.initialize(false);
	}

	@action.bound async onLookupSelect(eventProps) {
		this.suspend();
		const item = eventProps ? eventProps.dataItem || eventProps : null;
		this.selectedLookup = item;
		this.lookupDropdownStore.value = item;
		if (this.selectedLookup) {
			if (this.selectedLookup.parameters.lookup == 'channel-types' || this.selectedLookup.parameters.lookup == 'orderTypes') {
				this.tableData.actions = {};
			} else {
				this.tableData.actions = this.defaultActions;
			}

			if (!this.selectedLookup.parameters.fields) {
				this.addDefaultFieldData(this.selectedLookup.parameters);
			}

			this.setTableColumns(this.selectedLookup.parameters);
			this.initializeEditStoreFields(this.selectedLookup.parameters);

			// Init buttons
			let buttons = [];
			let itemName = translate(this.selectedLookup.parameters.itemName);
			let itemNames = makePlural(itemName);
			let { extraButton } = this.selectedLookup.parameters;

			if (this.permissions.create() && !this.selectedLookup.parameters.isEditDisabled) {
				buttons = [
					{
						className: 'btn btn--dropdown header__button',
						iconClassName: 'fa fa-plus spc--right--sml',
						onClick: this.routes.create,
						text: translate('NEW') + ' ' + _.startCase(itemName),
					},
				];
				if (extraButton) {
					buttons.push({
						className: 'btn btn--dropdown header__button',
						iconClassName: extraButton.iconClassName,
						onClick: extraButton.onClick,
						text: translate(extraButton.text),
					});
				}
			}
			this.setButtonComponent({ buttons: buttons });
			this.setSearchVisible();
		} else {
			this.setButtonComponent();
			this.setSearchVisible(false);
			this.initialize();
		}
		await this.queryUtility.initialize(false);
		// Filter persistance (save)
		if (this.selectedLookup) {
			localStorageHandler.set('lookup-filter-key', _.get(this.selectedLookup, 'parameters.lookup'));
		} else {
			localStorageHandler.remove('lookup-filter-key');
		}
		this.resume();
	}

	@action.bound closeEditModal() {
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			create: undefined,
			importData: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	@action.bound async createLookups(items) {
		try {
			await this.listStore.createLookups(items, this.selectedLookup.parameters.lookup, sourceOfChange.createLookup);
			this.rootStore.notificationStore.success(['LOOKUPIMPORT', { lookup: this.selectedLookup.parameters.itemName }]);
			await this.findLookup();
		} catch ({ statusCode, data }) {
			if (data && data.message) {
				if (statusCode === 409) {
					this.rootStore.notificationStore.warning([
						'LOOKUP.CONFLICT',
						{ lookup: this.selectedLookup.parameters.itemName },
					]);
				} else {
					this.rootStore.notificationStore.warning(t(data.message));
				}
			} else {
				this.rootStore.notificationStore.error([
					'LOOKUPIMPORTERROR',
					{ lookup: this.selectedLookup.parameters.itemName, code: statusCode },
				]);
			}
		}
	}

	@action.bound async findLookup() {
		var response = {};
		let filter = this.queryUtility.filter;
		if (this.selectedLookup) {
			this.suspend();
			if (this.selectedLookup.parameters.embed) {
				filter.embed = this.selectedLookup.parameters.embed;
			}
			await this.listStore.find(filter, this.selectedLookup.parameters.lookup);
			if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
				await this.queryUtility.changePage(1);
			}
			response = this.listStore.response;
			this.queryUtility.handleResponse(response);
			this.resume();
		}
	}

	@action async deleteLookup(item) {
		this.suspend();
		try {
			await this.listStore.deleteLookup({ id: item.id }, this.selectedLookup.parameters.lookup);
			this.rootStore.notificationStore.success([
				'LOOKUPDELETE.LOOKUP',
				{ name: item.name ? item.name : item[Object.keys(item)[1]], lookup: this.selectedLookup.parameters.itemName },
			]);
			await this.findLookup();
		} catch ({ statusCode, data }) {
			if (statusCode === 400) {
				this.rootStore.notificationStore.error([
					'LOOKUPDELETEERROR.LOOKUP',
					{
						lookup: this.selectedLookup.parameters.itemName,
						code: 'LOOKUPREFERENCEERROR',
					},
				]);
			} else {
				if (data && data.message) {
					this.rootStore.notificationStore.error(data.message);
				} else {
					this.rootStore.notificationStore.error([
						'LOOKUPDELETEERROR.LOOKUP',
						{ lookup: this.selectedLookup.parameters.itemName, code: statusCode },
					]);
				}
			}
		}
		this.resume();
	}

	setTableColumns = itemParameters => {
		this.tableData.columns = [];
		for (let i = 0; i < itemParameters.fields.length; i++) {
			let field = itemParameters.fields[i];
			this.tableData.columns[i] = {
				key: field.path ? field.path : field.key || 'name',
				title: field.label || itemParameters.columnName,
			};
		}
	};

	initializeEditStoreFields = itemParameters => {
		// Init edit store item name (create and edit)
		this.editView = {
			itemName: itemParameters.itemName,
			initFields: itemParameters.fields.map(field => {
				return Object.assign(
					{
						name: field.key || 'name',
						rules: field.rules || 'required|string',
						value: '',
						label: field.label || itemParameters.columnName,
					},
					field
				);
			}),
		};
	};

	addDefaultFieldData = itemParameters => {
		itemParameters.fields = [
			{
				key: 'name',
				label: itemParameters.columnName,
				rules: 'required|string',
			},
		];
	};
}

export default LookupViewStore;
